import { Add, Delete as DeleteIcon, FolderOpen } from "@mui/icons-material";
import { Box, Button, IconButton, TextField, Typography } from "@mui/material";
import type { Dispatch, SetStateAction } from "react";
import type { BackendUser } from "types/interfaces/adminUsers";
import {
  type FolderMountRow,
  newFolderMountRow,
  subfolderAliasError,
} from "./sftpFolderMountUtils";

export interface SftpFolderMountsEditorProps {
  readOnly?: boolean;
  bucket: string;
  setBucket: (v: string) => void;
  selectedUser: BackendUser | null;
  folderMountRows: FolderMountRow[];
  setFolderMountRows: Dispatch<SetStateAction<FolderMountRow[]>>;
  setFolderPickerRowIndex: (idx: number | null) => void;
}

export default function SftpFolderMountsEditor({
  readOnly = false,
  bucket,
  setBucket,
  selectedUser,
  folderMountRows,
  setFolderMountRows,
  setFolderPickerRowIndex,
}: SftpFolderMountsEditorProps) {
  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={700}
        sx={{ mb: 0.5, display: "block" }}
      >
        S3 locations *
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 1 }}
      >
        Each location can have its own SFTP folder name and optional subfolder
        filter. With one location, leave SFTP name empty to keep files at the
        session root.
      </Typography>
      {folderMountRows.map((row, idx) => (
        <Box
          key={row.id}
          sx={{
            mb: 1.5,
            p: 1.5,
            borderRadius: 1.5,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "rgba(31,93,107,0.04)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography
              variant="caption"
              fontWeight={800}
              color="text.secondary"
            >
              Location {idx + 1}
            </Typography>
            {!readOnly && folderMountRows.length > 1 ? (
              <IconButton
                size="small"
                aria-label="Remove location"
                onClick={() =>
                  setFolderMountRows((prev) => prev.filter((_, i) => i !== idx))
                }
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            ) : null}
          </Box>
          <TextField
            label={
              folderMountRows.length > 1
                ? "SFTP folder name *"
                : "SFTP folder name (optional)"
            }
            value={row.label}
            onChange={(e) => {
              const v = e.target.value;
              setFolderMountRows((prev) => {
                const next = [...prev];
                next[idx] = { ...next[idx], label: v };
                return next;
              });
            }}
            size="small"
            fullWidth
            disabled={readOnly}
            sx={{ mb: 1, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
            helperText={
              folderMountRows.length > 1
                ? "Name shown on SFTP (not the S3 path)"
                : "Leave blank to expose this prefix at the SFTP root"
            }
          />
          {bucket && row.prefix ? (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <FolderOpen fontSize="small" sx={{ color: "primary.main" }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "monospace",
                      fontSize: 12,
                      wordBreak: "break-all",
                    }}
                  >
                    {bucket}/{row.prefix}
                  </Typography>
                </Box>
                {!readOnly ? (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setFolderMountRows((prev) => {
                        const next = [...prev];
                        next[idx] = {
                          ...next[idx],
                          prefix: "",
                          subfolders: [],
                          subfolder_aliases: {},
                        };
                        return next;
                      });
                      const anyLeft = folderMountRows.some(
                        (m, i) => i !== idx && m.prefix,
                      );
                      if (!anyLeft) setBucket("");
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                ) : null}
              </Box>
              {row.subfolders.length > 0 ? (
                <Box sx={{ mt: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={700}
                    sx={{ display: "block", mb: 0.25 }}
                  >
                    Visible subfolders
                  </Typography>
                  {row.subfolders.map((sub) => {
                    const aliasErr = subfolderAliasError(row, sub);
                    return (
                      <Box
                        key={sub}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mt: 0.5,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            fontFamily: "monospace",
                            wordBreak: "break-all",
                          }}
                        >
                          {sub}
                        </Typography>
                        <TextField
                          size="small"
                          placeholder={sub}
                          value={row.subfolder_aliases[sub] ?? ""}
                          onChange={(e) => {
                            const v = e.target.value;
                            setFolderMountRows((prev) => {
                              const next = [...prev];
                              next[idx] = {
                                ...next[idx],
                                subfolder_aliases: {
                                  ...next[idx].subfolder_aliases,
                                  [sub]: v,
                                },
                              };
                              return next;
                            });
                          }}
                          error={!!aliasErr}
                          helperText={aliasErr || undefined}
                          disabled={readOnly}
                          inputProps={{
                            maxLength: 255,
                            "aria-label": `SFTP name for ${sub}`,
                          }}
                          sx={{
                            width: 180,
                            flexShrink: 0,
                            "& .MuiOutlinedInput-root": { borderRadius: 1.5 },
                            "& .MuiOutlinedInput-input": {
                              py: 0.5,
                              fontSize: 13,
                            },
                          }}
                        />
                      </Box>
                    );
                  })}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    SFTP name is optional — blank shows the S3 folder name.
                  </Typography>
                </Box>
              ) : null}
            </>
          ) : (
            <Button
              variant="outlined"
              fullWidth
              startIcon={<FolderOpen />}
              onClick={() => setFolderPickerRowIndex(idx)}
              disabled={readOnly || !selectedUser}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 1.5,
                justifyContent: "flex-start",
              }}
            >
              {selectedUser ? "Choose S3 prefix" : "Select a user first"}
            </Button>
          )}
          {!readOnly && bucket && row.prefix ? (
            <Button
              size="small"
              onClick={() => setFolderPickerRowIndex(idx)}
              sx={{ textTransform: "none", mt: 0.5, fontWeight: 600 }}
            >
              Change prefix or subfolders
            </Button>
          ) : null}
        </Box>
      ))}
      {!readOnly ? (
        <Button
          variant="text"
          size="small"
          startIcon={<Add />}
          onClick={() =>
            setFolderMountRows((prev) => [...prev, newFolderMountRow()])
          }
          disabled={!selectedUser}
          sx={{ textTransform: "none", fontWeight: 700, mt: 0.5 }}
        >
          Add another S3 location
        </Button>
      ) : null}
    </Box>
  );
}
